import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Mail, Loader2, ShieldCheck, KeyRound, ArrowRight, AlertCircle } from "lucide-react";

const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

export default function Login() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const loc = useLocation();
    const next = new URLSearchParams(loc.search).get("next") || "/dashboard";
    const { login, startGoogleLogin, user } = useAuth();

    const [email, setEmail] = useState("demo@nournet.sa");
    const [password, setPassword] = useState("demo123");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    React.useEffect(() => {
        if (user) {
            const dest = user.role === "admin" ? "/admin" : next;
            nav(dest, { replace: true });
        }
    }, [user, nav, next]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError("");
        try {
            await login(email, password);
            // Redirect handled by the effect once `user` is set
        } catch (err) {
            setError(err?.response?.data?.detail === "invalid_credentials"
                ? "Incorrect email or password."
                : "Login failed — try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="flex min-h-screen items-center justify-center bg-limestone-100 px-5 py-28" data-testid="login-page">
            <div className="mx-auto grid w-full max-w-5xl grid-cols-1 overflow-hidden rounded-card border-2 border-ink-200 bg-white lg:grid-cols-2">
                {/* LEFT: brand panel */}
                <div className="bg-redsea-grain relative border-b-2 border-ink-800 p-10 text-white lg:border-b-0 lg:border-e-2">
                    <div className="asir-stripe absolute top-0 inset-x-0 h-[6px]" aria-hidden="true" />
                    <p className="mt-4 text-[13px] font-bold uppercase tracking-[0.2em] text-souq-400">
                        NourNet · Sign in
                    </p>
                    <h1 className="mt-5 font-display text-[32px] lg:text-[44px] font-bold tracking-tight leading-tight">
                        Saudi-hosted identity. Built on Nafath-ready rails.
                    </h1>
                    <p className="mt-5 max-w-md text-[17px] leading-relaxed font-medium text-redsea-100">
                        One account for domains, DNS, and email. Session is encrypted, Saudi-hosted, and PDPL-compliant.
                    </p>

                    <ul className="mt-8 space-y-3.5 text-[15px] font-semibold">
                        <li className="flex items-center gap-2.5"><ShieldCheck className="h-5 w-5 text-souq-400" />Nafath identity — <span className="text-redsea-200">coming soon</span></li>
                        <li className="flex items-center gap-2.5"><KeyRound className="h-5 w-5 text-souq-400" />Google sign-in</li>
                        <li className="flex items-center gap-2.5"><Mail className="h-5 w-5 text-souq-400" />Email &amp; password</li>
                    </ul>

                    <Link to="/preview" data-testid="login-back" className="mt-10 inline-flex h-12 items-center gap-2 rounded-btn border-2 border-white/40 bg-white/5 px-4 text-[15px] font-bold text-white transition-colors duration-200 hover:bg-white/10">
                        <ArrowLeft className="h-4 w-4 rtl-mirror" /> Back to previews
                    </Link>
                </div>

                {/* RIGHT: form */}
                <div className="flex flex-col justify-center p-10 lg:p-12">
                    <h2 className="font-display text-[28px] font-bold tracking-tight text-ink-800">Sign in to NourNet</h2>
                    <p className="mt-2 text-[16px] font-medium text-ink-600">Use your Google account or the demo customer below.</p>

                    {/* Google OAuth — enabled only when GOOGLE_CLIENT_ID is configured. */}
                    {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                    {process.env.REACT_APP_GOOGLE_ENABLED === "1" ? (
                        <button
                            type="button"
                            onClick={() => startGoogleLogin(next)}
                            data-testid="login-google-btn"
                            className="mt-7 inline-flex h-[56px] w-full items-center justify-center gap-3 rounded-btn border-2 border-ink-800 bg-white px-5 text-[16px] font-bold text-ink-800 transition-all duration-200 hover:bg-ink-800 hover:text-white active:translate-y-[1px]"
                        >
                            <GoogleIcon />
                            Continue with Google
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled
                            data-testid="login-google-btn-soon"
                            className="mt-7 inline-flex h-[56px] w-full items-center justify-center gap-3 rounded-btn border-2 border-ink-300 bg-mudbrick-100 px-5 text-[16px] font-bold text-ink-500 cursor-not-allowed"
                        >
                            <GoogleIcon />
                            Continue with Google <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-madder-500">soon</span>
                        </button>
                    )}

                    <button
                        type="button"
                        disabled
                        data-testid="login-nafath-btn"
                        className="mt-3 inline-flex h-[56px] w-full items-center justify-center gap-3 rounded-btn border-2 border-ink-300 bg-mudbrick-100 px-5 text-[16px] font-bold text-ink-500 cursor-not-allowed"
                    >
                        <ShieldCheck className="h-5 w-5" />
                        Continue with Nafath <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-madder-500">soon</span>
                    </button>

                    <div className="my-7 flex items-center gap-4 text-[12px] font-bold uppercase tracking-[0.18em] text-ink-500">
                        <span className="h-px flex-1 bg-ink-200" />
                        or email
                        <span className="h-px flex-1 bg-ink-200" />
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="login-password-form">
                        <div>
                            <label htmlFor="login-email" className="mb-2 block text-[13px] font-bold uppercase tracking-[0.14em] text-ink-700">Email</label>
                            <input
                                id="login-email" type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="h-[56px] w-full rounded-btn border-2 border-ink-300 bg-white px-4 text-[16px] font-medium text-ink-800 transition-colors duration-200 focus:border-ink-800 focus:outline-none num-ltr"
                            />
                        </div>
                        <div>
                            <label htmlFor="login-password" className="mb-2 block text-[13px] font-bold uppercase tracking-[0.14em] text-ink-700">Password</label>
                            <input
                                id="login-password" type="password" required value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                className="h-[56px] w-full rounded-btn border-2 border-ink-300 bg-white px-4 text-[16px] font-medium text-ink-800 transition-colors duration-200 focus:border-ink-800 focus:outline-none"
                            />
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 rounded-[8px] border-2 border-madder-500 bg-madder-50 p-3 text-[14px] font-semibold text-madder-700" data-testid="login-error">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit" disabled={loading} data-testid="login-submit"
                            className="inline-flex h-[60px] w-full items-center justify-center gap-2 rounded-btn bg-madder-500 px-6 text-[17px] font-bold text-white shadow-madder transition-all duration-200 hover:bg-madder-600 active:translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? <><Loader2 className="h-5 w-5 animate-spin" />Signing in…</> : <>Sign in<ArrowRight className="h-5 w-5 rtl-mirror" /></>}
                        </button>
                    </form>

                    <div className="mt-6 rounded-[10px] border-2 border-dashed border-ink-200 bg-mudbrick-100 p-4 text-[13px] text-ink-700">
                        <p className="font-bold text-ink-800">Test credentials</p>
                        <p className="mt-1 font-mono num-ltr">Customer: <span className="text-madder-500">demo@nournet.sa / demo123</span></p>
                        <p className="font-mono num-ltr">Admin: <span className="text-redsea-500">admin@nournet.sa / admin123</span></p>
                    </div>
                </div>
            </div>
        </main>
    );
}
